import { useRef, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";

import { REACTIONS } from "./reactions";

import type { ApiErrorCode } from "../../api/client";
import type {
  GalleryPostCard,
  GalleryReactionKind,
  GalleryReactionState,
} from "@canvaschool/contracts";

/**
 * Five reaction bubbles, always visible (a bubble with 0 must be pressable).
 * A press asks the server and then shows the counts it answered with.
 */
export const ReactionBar = ({
  post,
  showLabels = false,
  onChange,
  onError,
}: {
  post: Pick<GalleryPostCard, "id" | "reactions" | "myReactions">;
  showLabels?: boolean;
  onChange: (state: GalleryReactionState) => void;
  onError: (code: ApiErrorCode) => void;
}) => {
  const auth = useAuth();
  const { api } = auth;
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);

  const toggle = async (kind: GalleryReactionKind) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    setPending(true);
    try {
      const state = await api.setGalleryReaction(
        post.id,
        kind,
        !post.myReactions.includes(kind),
      );
      onChange(state);
    } catch (failure) {
      onError(auth.handleError(failure));
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return (
    <div
      className="canvaschool-gallery-reactions"
      role="group"
      aria-label="반응 남기기"
    >
      {REACTIONS.map(({ kind, emoji, label }) => {
        const pressed = post.myReactions.includes(kind);
        return (
          <button
            key={kind}
            type="button"
            className={
              pressed
                ? "canvaschool-gallery-bubble is-pressed"
                : "canvaschool-gallery-bubble"
            }
            aria-pressed={pressed}
            aria-label={`${label} ${post.reactions[kind]}`}
            title={label}
            disabled={pending}
            onClick={() => void toggle(kind)}
          >
            <span aria-hidden="true">{emoji}</span>
            {showLabels && (
              <span className="canvaschool-gallery-bubble-label" aria-hidden>
                {label}
              </span>
            )}
            <span className="canvaschool-gallery-bubble-count" aria-hidden>
              {post.reactions[kind]}
            </span>
          </button>
        );
      })}
    </div>
  );
};
